'use client'

import React from 'react'
import { Link } from '@repo/core/navigation'
import { ArrowClockwiseRegular, HomeRegular, WarningRegular } from '@fluentui/react-icons'
import { useTranslations } from '@repo/core/i18n'
import { cn, Button } from '@repo/ui'
import { VStack, HStack } from '@repo/ui'

export interface ErrorPageProps {
  /**
   * Error object
   */
  error?: Error & { digest?: string }

  /**
   * Custom title for the error page
   * @default "Something went wrong"
   */
  title?: string

  /**
   * Custom message to display
   */
  message?: string

  /**
   * Show error details (stack trace, etc.)
   * @default false in production, true in development
   */
  showDetails?: boolean

  /**
   * Reset/retry callback
   */
  onReset?: () => void

  /**
   * Show reset button
   * @default true
   */
  showResetButton?: boolean

  /**
   * Show home link
   * @default true
   */
  showHomeLink?: boolean

  /**
   * Custom className for the container
   */
  className?: string
}

/**
 * Generic error page component
 * Used by error boundaries to display error states
 *
 * @example
 * ```tsx
 * <ErrorPage
 *   error={error}
 *   onReset={reset}
 * />
 * ```
 */
export function ErrorPage({
  error,
  title = 'Something went wrong',
  message,
  showDetails = process.env['NODE_ENV'] === 'development',
  onReset,
  showResetButton = true,
  showHomeLink = true,
  className,
}: ErrorPageProps) {
  const t = useTranslations('errors')

  const displayMessage =
    message ||
    error?.message ||
    t('unexpectedError')

  const isDevelopment = process.env['NODE_ENV'] === 'development'

  return (
    <VStack
      align="center"
      spacing="none"
      className={cn(
        'min-h-screen justify-center px-4 py-12',
        className
      )}
    >
      <div className="mx-auto max-w-2xl text-center">
        {/* Error Icon */}
        <HStack
          spacing="none"
          className="mx-auto h-20 w-20 rounded-full bg-destructive/10 justify-center"
        >
          <WarningRegular className="h-10 w-10 text-destructive" />
        </HStack>

        {/* Title */}
        <h1 className="mt-6 text-3xl font-bold tracking-tight">{title}</h1>

        {/* Message */}
        <p className="mt-4 text-lg text-muted-foreground">{displayMessage}</p>

        {/* Error Digest (for logging/support) */}
        {error?.digest && (
          <p className="mt-2 text-sm text-muted-foreground">
            {t('errorId')}: <code className="rounded bg-muted px-1 py-0.5">{error.digest}</code>
          </p>
        )}

        {/* Actions */}
        <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          {showResetButton && onReset && (
            <Button
              onClick={onReset}
              className="inline-flex items-center gap-2"
            >
              <ArrowClockwiseRegular className="h-4 w-4" />
              {t('tryAgain')}
            </Button>
          )}

          {showHomeLink && (
            <Link
              href="/"
              className="inline-flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              <HomeRegular className="h-4 w-4" />
              {t('goHome')}
            </Link>
          )}
        </div>

        {/* Error Details (Development Only) */}
        {showDetails && error && (
          <details className="mt-12 rounded-lg border border-border bg-muted/50 p-6 text-left">
            <summary className="cursor-pointer text-sm font-medium">
              {t('errorDetails')} {isDevelopment && '(Development Only)'}
            </summary>
            <div className="mt-4 space-y-4">
              {/* Error Name */}
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  {t('errorType')}
                </div>
                <code className="mt-1 block text-sm">{error.name || 'Error'}</code>
              </div>

              {/* Error Message */}
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  {t('message')}
                </div>
                <code className="mt-1 block text-sm">{error.message}</code>
              </div>

              {/* Stack Trace */}
              {error.stack && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">
                    {t('stackTrace')}
                  </div>
                  <pre className="mt-1 max-h-64 overflow-auto rounded bg-background p-2 text-xs">
                    {error.stack}
                  </pre>
                </div>
              )}

              {/* Digest */}
              {error.digest && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">
                    {t('digest')}
                  </div>
                  <code className="mt-1 block text-sm">{error.digest}</code>
                </div>
              )}
            </div>
          </details>
        )}

        {/* Support Message */}
        <p className="mt-8 text-sm text-muted-foreground">
          {t('supportMessage')}{' '}
          <a href={import.meta.env.BASE_URL + 'help'} className="underline hover:text-foreground">
            {t('support')}
          </a>
          .
        </p>
      </div>
    </VStack>
  )
}
